/**
 * Map Overlays Panel
 * Right sidebar for toggling area/overlay visibility on the project map.
 * Displays project-specific areas (from API), falling back to global config.
 */

import React from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Layers as LayersIcon,
} from '@mui/icons-material';

import SpriteIcon from './SpriteIcon';
import { colorPalette } from '../../theme';

// Helper to render area icon with fallback
const AreaIcon = ({ area }) => {
  const iconName = area.icon
    ? area.icon.replace('.png', '_Marker.png')
    : `${(area.name || '').replace(/\s+/g, '_')}_Marker.png`;

  return (
    <Box sx={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <SpriteIcon iconName={iconName} size={40} />
    </Box>
  );
};

const MapOverlaysPanel = ({ open, onClose, visibleOverlays, onToggleOverlay, onToggleAll, projectAreas = [], embedded = false, manualCounts = null }) => {
  if (!open) return null;

  // Use project-specific areas if available, otherwise empty
  const areas = projectAreas;

  const allChecked = areas.length > 0 && areas.every((area) =>
    visibleOverlays.includes(area.id)
  );
  const someChecked = areas.some((area) =>
    visibleOverlays.includes(area.id)
  );

  const containerStyles = embedded ? {
    width: '100%',
    backgroundColor: 'transparent',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    position: 'relative',
  } : {
    width: 400,
    backgroundColor: colorPalette.backgroundColor,
    borderLeft: `1px solid ${colorPalette.lightSteel}`,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    zIndex: 5,
  };

  return (
    <Box sx={containerStyles}>
      {/* Header - only show when not embedded */}
      {!embedded && (
        <Box sx={{ p: 6, display: 'flex', alignItems: 'center', gap: 1 }}>
          <LayersIcon sx={{ color: colorPalette.portlandCement }} />
          <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1, color: colorPalette.portlandCement }}>
            Map Overlays
          </Typography>
        </Box>
      )}

      {/* AST-89: manual mat count pills on structures. Manual projects only
          (the parent passes null otherwise), on by default. */}
      {manualCounts && (
        <Box sx={{ px: 6, pt: 2 }}>
          <FormControlLabel
            control={<Checkbox checked={manualCounts.checked} onChange={manualCounts.onToggle} />}
            label={<Typography variant="body2">Manual mat counts</Typography>}
            sx={{ ml: 0, gap: 1, minHeight: 44 }}
          />
        </Box>
      )}

      {/* Select/Unselect All */}
      <Box sx={{ px: 6, py: 4 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={allChecked}
              indeterminate={someChecked && !allChecked}
              onChange={() => onToggleAll(!allChecked)}
            />
          }
          label={
            <Typography variant="body2">
              Select all
            </Typography>
          }
          sx={{ ml: 0, gap: 1 }}
        />
      </Box>

      {/* Area List -- grouped by name */}
      <Box sx={{ px: 6, py: 1 }}>
        {areas.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No areas mapped for this project.
          </Typography>
        ) : (
          (() => {
            // Group areas by name
            const groups = {};
            for (const area of areas) {
              const key = area.name || 'Unknown';
              if (!groups[key]) groups[key] = { name: key, icon: area.icon, areas: [] };
              groups[key].areas.push(area);
            }
            return Object.values(groups).map((group) => {
              const groupIds = group.areas.map(a => a.id);
              const allGroupChecked = groupIds.every(gid => visibleOverlays.includes(gid));
              const someGroupChecked = groupIds.some(gid => visibleOverlays.includes(gid));
              return (
                <FormControlLabel
                  key={group.name}
                  control={
                    <Checkbox
                      checked={allGroupChecked}
                      indeterminate={someGroupChecked && !allGroupChecked}
                      onChange={() => {
                        // Toggle all IDs in this group
                        for (const gid of groupIds) {
                          onToggleOverlay(gid);
                        }
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AreaIcon area={group.areas[0]} />
                      <Typography variant="body2">{group.name}</Typography>
                    </Box>
                  }
                  sx={{ display: 'flex', width: '100%', ml: 0, mb: 1, minHeight: 44, gap: 1 }}
                />
              );
            });
          })()
        )}
      </Box>
    </Box>
  );
};

export default MapOverlaysPanel;
